import React, { useState, useEffect, useReducer, useContext } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Receipt,
  ExternalLink,
  Loader2,
  Settings2,
  Info,
  RefreshCw,
  FilePlus2,
  Download,
  Ban,
} from "lucide-react";

import openSocket from "../../services/socket-io";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import toastError from "../../errors/toastError";
import { formatMoney } from "../../helpers/money";
import { AuthContext } from "../../context/Auth/AuthContext";
import { checkPermission } from "../../rules";
import PaymentMethodPicker from "../../components/PaymentMethodPicker";
import useBillingStatus from "../../hooks/useBillingStatus";
import ManualInvoiceModal from "../../components/ManualInvoiceModal";

const STATUS = {
  pending: {
    label: "Pendiente",
    badge:
      "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  },
  paid: {
    label: "Pagada",
    badge:
      "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
  },
  overdue: {
    label: "Vencida",
    badge:
      "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300 border-red-200 dark:border-red-800",
  },
  failed: {
    label: "Fallida",
    badge:
      "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300 border-red-200 dark:border-red-800",
  },
  void: {
    label: "Anulada",
    badge:
      "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700",
  },
};

const statusOf = (status) => STATUS[status] || STATUS.pending;

const reducer = (state, action) => {
  if (action.type === "LOAD") {
    const incoming = action.payload;
    const fresh = [];
    incoming.forEach((invoice) => {
      const idx = state.findIndex((i) => i.id === invoice.id);
      if (idx !== -1) state[idx] = invoice;
      else fresh.push(invoice);
    });
    return [...state, ...fresh];
  }
  if (action.type === "UPDATE") {
    const invoice = action.payload;
    const idx = state.findIndex((i) => i.id === invoice.id);
    if (idx !== -1) {
      const next = [...state];
      next[idx] = invoice;
      return next;
    }
    return [invoice, ...state];
  }
  if (action.type === "RESET") return [];
  return state;
};

const formatPeriod = (start, end) => {
  const options = { day: "numeric", month: "short", year: "numeric" };
  const from = new Date(start).toLocaleDateString("es-DO", options);
  const to = new Date(end).toLocaleDateString("es-DO", options);
  return `${from} — ${to}`;
};

const Billing = () => {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [invoices, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [runningCycle, setRunningCycle] = useState(false);
  const [voidingId, setVoidingId] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  // Config del plan: solo la usa el modal de factura manual para prerrellenar
  // moneda, ITBIS y días de vencimiento con los mismos valores del plan.
  const [config, setConfig] = useState(null);

  const canPay =
    user.profile === "admin" || checkPermission(user.permissions, "billing:pay");

  const { status: billing } = useBillingStatus();
  const isOwner = !!billing?.isOwner;

  useEffect(() => {
    if (!isOwner) return;
    api
      .get("/billing/config")
      .then(({ data }) => setConfig(data))
      // No es crítico: el modal cae a sus valores por defecto.
      .catch(() => setConfig(null));
  }, [isOwner]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/billing/invoices", {
          params: { pageNumber },
        });
        dispatch({ type: "LOAD", payload: data.invoices });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    const handleBilling = (data) => {
      if (data.invoice) dispatch({ type: "UPDATE", payload: data.invoice });
    };

    socket.on("billing", handleBilling);
    return () => socket.off("billing", handleBilling);
  }, []);

  const handlePay = async (invoice) => {
    setPayingId(invoice.id);
    try {
      const { data } = await api.post(`/billing/invoices/${invoice.id}/checkout`);
      // Same tab: the gateway redirects back to /factura/:token when done.
      window.location.href = data.url;
    } catch (err) {
      toastError(err);
      setPayingId(null);
    }
  };

  const reloadFirstPage = async () => {
    try {
      const { data } = await api.get("/billing/invoices", {
        params: { pageNumber: 1 },
      });
      dispatch({ type: "LOAD", payload: data.invoices });
    } catch (err) {
      toastError(err);
    }
  };

  /**
   * Corre el ciclo de facturación en el momento. Informa lo que pasó — incluido
   * el caso en que no hizo nada — para no dejar al dueño esperando sin saber si
   * funcionó.
   */
  const handleRunCycle = async () => {
    setRunningCycle(true);
    try {
      const { data } = await api.post("/billing/run-cycle");

      if (data.skipped) toast.warn(data.skipped);
      else if (data.invoiceCreated)
        toast.success(`Factura ${data.invoiceCreated} emitida`);
      else if (data.invoiceExisting)
        toast.info(
          `La factura ${data.invoiceExisting} de este período ya estaba emitida`
        );
      else toast.info("El ciclo corrió sin novedades");

      if (data.chargesApplied > 0)
        toast.success(`${data.chargesApplied} cobro(s) aplicado(s)`);

      await reloadFirstPage();
    } catch (err) {
      toastError(err);
    } finally {
      setRunningCycle(false);
    }
  };

  /**
   * Anula una factura (exonerar el pago). Se pide el motivo porque queda
   * impreso en el documento: una factura sin cobrar y sin explicación es un
   * agujero en la contabilidad.
   */
  const handleVoid = async (invoice) => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt(
      `Anular la factura ${invoice.number} por ${formatMoney(
        invoice.totalCents,
        invoice.currency
      )}?\n\nEl cliente deja de deberla y no se le cobrará. Motivo (se imprime en la factura):`
    );

    // null = canceló el diálogo. Cadena vacía = aceptó sin motivo, se permite.
    if (reason === null) return;

    setVoidingId(invoice.id);
    try {
      const { data } = await api.post(`/billing/invoices/${invoice.id}/void`, {
        reason: reason || undefined,
      });

      if (data.alreadyVoid) toast.info("Esa factura ya estaba anulada");
      else toast.success(`Factura ${invoice.number} anulada`);

      if (data.suspensionLifted)
        toast.success("Se levantó la suspensión del acceso");

      await reloadFirstPage();
    } catch (err) {
      toastError(err);
    } finally {
      setVoidingId(null);
    }
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight)
      setPageNumber((p) => p + 1);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Facturación</Title>
        <MainHeaderButtonsWrapper>
          {isOwner && (
            <>
              <button
                onClick={handleRunCycle}
                disabled={runningCycle}
                title="Emitir la factura del período sin esperar el ciclo automático"
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {runningCycle ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Generar factura ahora
              </button>
              <button
                onClick={() => setManualOpen(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <FilePlus2 className="w-4 h-4" />
                Factura manual
              </button>
              <button
                onClick={() => history.push("/billing-settings")}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <Settings2 className="w-4 h-4" />
                Configuración
              </button>
            </>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className="flex-1 overflow-y-auto px-3 sm:px-6 pb-6" onScroll={handleScroll}>
        {/* Fail closed, but never silently: the admin who came looking for the
            settings deserves to know why they are not here. */}
        {billing?.migrationsPending && billing?.ownerHint && (
          <div className="mb-4 flex items-start gap-3 p-4 rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30">
            <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-800 dark:text-amber-300">
              <p className="font-semibold mb-1">Facturación sin inicializar</p>
              <p>{billing.ownerHint}</p>
            </div>
          </div>
        )}

        {!billing?.migrationsPending && billing?.ownerHint && (
          <div className="mb-4 flex items-start gap-3 p-4 rounded-xl border border-indigo-200 dark:border-indigo-900 bg-indigo-50 dark:bg-indigo-950/30">
            <Info className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-indigo-800 dark:text-indigo-300">
              <p className="font-semibold mb-1">Recomendación de seguridad</p>
              <p>{billing.ownerHint}</p>
              <code className="mt-2 block px-2 py-1 rounded bg-white/60 dark:bg-slate-900/60 font-mono text-[11px]">
                BILLING_OWNER_EMAIL={user.email || "tu@correo.com"}
              </code>
            </div>
          </div>
        )}

        <PaymentMethodPicker canPay={canPay} />

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                {["Factura", "Período", "Vence", "Total", "Estado", ""].map(
                  (header, idx) => (
                    <th
                      key={header || idx}
                      className={`px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap ${
                        idx >= 3 ? "text-right" : "text-left"
                      }`}
                    >
                      {header}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {invoices.length === 0 && !loading ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center">
                    <Receipt className="w-10 h-10 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
                    <p className="text-base font-semibold text-slate-500 dark:text-slate-400">
                      Aún no hay facturas
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                      Aquí aparecerán todas tus facturas cuando se emitan
                    </p>
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => {
                  const meta = statusOf(invoice.status);
                  const payable =
                    invoice.status === "pending" || invoice.status === "overdue";
                  return (
                    <tr
                      key={invoice.id}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <p className="font-mono font-semibold text-slate-900 dark:text-slate-100">
                          {invoice.number}
                        </p>
                        {invoice.ncf && (
                          <p className="text-[10px] text-slate-400">
                            NCF {invoice.ncf}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                        {formatPeriod(invoice.periodStart, invoice.periodEnd)}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                        {new Date(invoice.dueAt).toLocaleDateString("es-DO")}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
                        {formatMoney(invoice.totalCents, invoice.currency)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border ${meta.badge}`}
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1.5">
                          {/* Documento imprimible: se abre en otra pestaña y el
                              navegador ofrece guardarlo como PDF. Sirve para
                              cualquier estado, no solo las pagadas. */}
                          <a
                            href={`${getBackendUrl()}/public/billing/invoices/${invoice.publicToken}/documento`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Descargar factura"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                          >
                            <Download className="w-3.5 h-3.5" />
                            Factura
                          </a>
                          {isOwner && payable && (
                            <button
                              onClick={() => handleVoid(invoice)}
                              disabled={voidingId === invoice.id}
                              title="Anular la factura (exonerar el pago)"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 dark:hover:bg-rose-950/30 dark:hover:text-rose-400 disabled:opacity-50 transition-colors"
                            >
                              {voidingId === invoice.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Ban className="w-3.5 h-3.5" />
                              )}
                              Anular
                            </button>
                          )}
                          {payable && (
                            <button
                              onClick={() => handlePay(invoice)}
                              disabled={payingId === invoice.id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                            >
                              {payingId === invoice.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <ExternalLink className="w-3.5 h-3.5" />
                              )}
                              Pagar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
              {loading && (
                <tr>
                  <td colSpan={6} className="py-4 text-center">
                    <div className="flex justify-center">
                      <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <ManualInvoiceModal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onCreated={reloadFirstPage}
        config={config}
      />
    </MainContainer>
  );
};

export default Billing;
